import React, { useRef } from 'react';
import { Group } from 'react-konva';
import Grabber from '../../grabber';
import {
    distance,
    calculatePosition,
    angle as calculateAngle,
    toDegrees,
    mod,
    getWidthFromDiameterAndAngle,
} from 'src/util/math';
import type { KonvaEventObject } from 'konva/lib/Node';
import type { CylinderType } from 'src/types/transforms';
import { useDragGesture } from 'src/hooks/useDragGesture';

type Point = {
    x: number;
    y: number;
};

type Props = {
    container: HTMLElement;
    scaledCylinderWarp: Omit<CylinderType, 'id' | 'fullWrap'>;
    cylinderWarp: CylinderType;
    scale: number;
    onDragMove: (warp: CylinderType) => void;
    onDragEnd: () => void;
    cursorChange: (cursor: string) => void;
    offset: Point;
};

const RadiusGrabbers: React.FC<Props> = (props) => {
    const {
        container,
        scaledCylinderWarp,
        cylinderWarp,
        scale,
        onDragMove: onDragMoveProp,
        onDragEnd,
        cursorChange,
        offset,
    } = props;

    const startCylinderWarpRef = useRef<CylinderType | null>(null);
    // Which handle (reference angle vs. extent angle) is being dragged -- set once at drag-start,
    // read by the single `handleDragMove` for the duration of that gesture.
    const extentRef = useRef(false);

    const handleDragMove = (e: MouseEvent): void => {
        const extent = extentRef.current;
        const warp = JSON.parse(JSON.stringify(startCylinderWarpRef.current));

        const containerRect = container.getBoundingClientRect();
        const mousePosition = {
            x: (e.clientX - containerRect.left - offset.x) / scale,
            y: (e.clientY - containerRect.top - offset.y) / scale,
        };

        let angle = toDegrees(
            calculateAngle(warp.cylinderTop.center, calculatePosition(warp.cylinderTop, 0), mousePosition),
        );

        // rotate left or rotate right
        if (
            distance(mousePosition, calculatePosition(warp.cylinderTop, 270)) >
            distance(mousePosition, calculatePosition(warp.cylinderTop, 90))
        ) {
            angle = 360 - angle;
        }

        if (warp.decorationExtentDegreesBeta + angle < 0) {
            angle = 0;
        }

        if (extent) {
            if (angle < warp.referenceAngle) {
                warp.decorationExtentDegreesBeta = mod(angle + (360 - warp.decorationExtentDegreesAlpha), 360);
            } else {
                warp.decorationExtentDegreesBeta = mod(angle - warp.decorationExtentDegreesAlpha, 360);
            }
            const decoSize = getWidthFromDiameterAndAngle(warp.productDiameter, warp.decorationExtentDegreesBeta);
            warp.decorationAreaDimensions = { width: decoSize, height: decoSize };
        } else {
            warp.decorationExtentDegreesAlpha = angle;
        }

        onDragMoveProp(warp);
    };

    const { start } = useDragGesture({ onDragMove: handleDragMove, onDragEnd });

    const onDragStart = (e: KonvaEventObject<MouseEvent>, extent: boolean): void => {
        startCylinderWarpRef.current = cylinderWarp;
        extentRef.current = extent;

        start();
        e.cancelBubble = true;
    };

    const referencePoint = calculatePosition(
        scaledCylinderWarp.cylinderTop,
        -scaledCylinderWarp.decorationExtentDegreesAlpha,
    );
    const extentPoint = calculatePosition(
        scaledCylinderWarp.cylinderTop,
        -(scaledCylinderWarp.decorationExtentDegreesAlpha + scaledCylinderWarp.decorationExtentDegreesBeta),
    );

    return (
        <Group>
            <Grabber
                position={referencePoint}
                cursorChange={cursorChange}
                onDragStart={(e) => onDragStart(e, false)}
                color='purple'
            />
            {!cylinderWarp.fullWrap && (
                <Grabber
                    position={extentPoint}
                    cursorChange={cursorChange}
                    onDragStart={(e) => onDragStart(e, true)}
                    color='purple'
                />
            )}
        </Group>
    );
};

export default RadiusGrabbers;
